import type { StudentQuestionAnswer } from "p6m-p6u";
import { ExerciseTypes, Exercises } from "../enums/exercises";
import { ActivityVisibility, resolveTestActivityVisibility } from "./ActivityVisibility";

interface ActivityBlockedParams {
    visibilityDate: number | null | undefined;
    /** Legacy activities predate visibility dates — a null date there means "always on", not hidden. */
    isLegacy: boolean;
    isOwner: boolean;
    /**
     * The create-flow preview builds its draft without a `visibilityDate`, which resolves to
     * HIDDEN rather than "unconstrained" — without this every shared test blocks its own teacher.
     */
    isPreview?: boolean;
}

export const isActivityBlockedForStudent = (params: ActivityBlockedParams): boolean => {
    if (params.isLegacy || params.isOwner || params.isPreview) return false;
    return resolveTestActivityVisibility(params.visibilityDate).visibility !== ActivityVisibility.VISIBLE;
};

export enum StudentTestAccessStates {
    PENDING = "PENDING",
    ALREADY_SUBMITTED = "ALREADY_SUBMITTED",
    BLOCKED = "BLOCKED",
    ALLOWED = "ALLOWED",
}

interface StudentTestAccessParams {
    isResultCheckPending: boolean;
    hasAlreadySubmitted: boolean;
    isBlockedByVisibility: boolean;
}

/**
 * Order matters: an existing submission outranks visibility, because a student who legitimately
 * submitted before the teacher hid the test should still reach their outro rather than a "hidden" card.
 */
export const resolveStudentTestAccess = (params: StudentTestAccessParams): StudentTestAccessStates => {
    if (params.isResultCheckPending) return StudentTestAccessStates.PENDING;
    if (params.hasAlreadySubmitted) return StudentTestAccessStates.ALREADY_SUBMITTED;
    if (params.isBlockedByVisibility) return StudentTestAccessStates.BLOCKED;
    return StudentTestAccessStates.ALLOWED;
};

interface PersistedAnswersOwnerParams {
    isLoginFreeActivity: boolean;
    isPrivateAccess: boolean;
    classMemberId: string | null;
}

/**
 * The identity that persisted in-progress answers are tied to. Only login-gated activities accessed
 * by a member of the activity's own private class may be tied to that member — login-free activities
 * (legacy tests, public-class practices) always persist anonymously. Tying them to a member would
 * make the record unrestorable once the member session is gone, because there is no login step to
 * recover through.
 */
export const getPersistedAnswersOwner = (params: PersistedAnswersOwnerParams): string | null =>
    !params.isLoginFreeActivity && params.isPrivateAccess ? params.classMemberId : null;

export const getExerciseProgress = (answer?: StudentQuestionAnswer, questionMode?: string): number => {
    if (!answer) return 0;

    if (questionMode === ExerciseTypes.get(Exercises.FREE_TEXT)) {
        return (answer.textAnswer ?? "").trim() ? 100 : 0;
    }

    if (questionMode === ExerciseTypes.get(Exercises.CREATE_TEXT)) {
        return (answer.jumbledWordsAnswer ?? "").trim() ? 100 : 0;
    }

    const answers = answer.studentAnswers ?? [];
    const total = answers.length;
    const filled = answers.filter((a) => a.studentAnswer?.trim()).length;
    return total > 0 ? (filled / total) * 100 : 0;
};

/** The gap is a run of underscores; the parts around it are the sentence text. */
export const parseGapSentence = (gapSentence: string): string[] => gapSentence.split(/_+/);

export const focusNextOrBlur = (refs: (HTMLTextAreaElement | null)[], index: number) => {
    if (index + 1 < refs.length) {
        refs[index + 1]?.focus();
    } else {
        refs[index]?.blur();
    }
};
